import { WORLD_W, type CrateState, type GateDef, type InputState, type Platform, type PlayerState, type Rect, type WorldState } from '../../shared/types';
import { PHYSICS, isGateOpen } from '../../shared/simulation';
import { sampleBodyInterval } from './sampling';
import { mix } from './paint';
import type { TrajectorySample } from './movement';
import type { RemoteSample } from './timeline';

const EPS = 1e-7;
const CONTACT = 1e-3;
/** A rebase or anchor change is absorbed over about this long. */
const DECAY_MS = 120;
/** Floor decay rates (px/s): a body in motion hides a gentler decay; a still body settles quickly. */
const MIN_RATE_MOVING = 30;
const MIN_RATE_STILL = 60;
/** While the trajectory moves, the drawn step keeps at least this share of it in its own direction. */
const STEP_FLOOR = 0.4;
/** Rendered overlaps beyond this (both axes) are separated; below it is sub-pixel contact noise. */
const OVERLAP_PX = 1;
/** A predicted body whose trajectory moves less than this (px) between two render ticks is at rest. */
const REST_STEP = 0.005;
/** A horizontal residual held on a resting local body up to this size (px); beyond it the drawn body is
 * already visibly elsewhere (a resync-scale error) and is corrected at once rather than kept. */
const REST_HOLD_LIMIT = 24;
/** A body the courier stopped pushing waits where it was drawn until its delayed drawing arrives within this (px)... */
const HOLD_TOLERANCE = 0.25;
/** ...or crosses it, or moves away from it for this many consecutive frames, or — having moved during the
 * hold — comes to rest short of it for this many frames, or this long has passed since the last push. */
const HOLD_AWAY_FRAMES = 3;
const HOLD_SETTLED_FRAMES = 6;
const HOLD_TIMEOUT_MS = 1000;

type Body = PlayerState | CrateState;
export interface Pose { x: number; y: number }
export interface PlacedBody extends Pose { id: string; vx: number; vy: number; grounded: boolean; supportId: string | null; path: 'self' | 'group' | 'anchored' | 'remote' | 'held'; interpolated: Pose; offset: Pose }
/** `pushed`: group members (and their riders) that joined because the group displaces them, not because they ride the courier. */
export interface Placement { anchorId: string | null; pressing: boolean; group: Set<string>; pushed: Set<string> }
export interface PlacedScene {
  players: Map<string, PlacedBody>;
  crates: Map<string, PlacedBody>;
  placement: Placement | null;
  cornerTransitions: number;
  cornerCorrection: number;
}
export interface PlaceArguments {
  authority: WorldState;
  localId: string | null;
  input: InputState;
  remote: RemoteSample;
  trajectory: TrajectorySample | null;
  /** The current authority/journal evaluated at the previous frame's render tick (for folds and steps). */
  trajectoryPrevious: TrajectorySample | null;
  platforms: Platform[];
  platformsRemote: Platform[];
  gates: GateDef[];
  dtMs: number;
}

const isPlayer = (body: Body): body is PlayerState => 'supportId' in body;
const activePlayer = (p: PlayerState) => p.connected && !p.entered;
const overlapX = (a: Rect, b: Rect) => Math.min(a.x + a.w, b.x + b.w) - Math.max(a.x, b.x);
const overlapY = (a: Rect, b: Rect) => Math.min(a.y + a.h, b.y + b.h) - Math.max(a.y, b.y);

export function predictedBodies(world: WorldState): Body[] {
  return [...world.players.filter(activePlayer), ...world.crates];
}

/** The body this one rests on: its authoritative support when that is a body, else exact contact
 * from above (a rider whose feet touch a head, including two bodies flying in contact). */
function supportOf(body: Body, bodies: Body[]): Body | null {
  if (isPlayer(body) && body.grounded && body.supportId) {
    const named = bodies.find(other => other.id === body.supportId && other !== body);
    if (named) return named;
    if (body.supportId) return null; // a platform
  }
  const feet = body.y + body.h;
  let best: Body | null = null, bestOverlap = 0;
  for (const other of bodies) {
    if (other === body || Math.abs(feet - other.y) > CONTACT) continue;
    const overlap = overlapX(body, other);
    if (overlap > EPS && overlap > bestOverlap) { best = other; bestOverlap = overlap; }
  }
  return best;
}

/**
 * Which bodies move with the local courier's predicted trajectory (its riders, the crates it
 * leans on and every body the group's motion displaces through a side contact — pushed
 * couriers, chains of them, their riders) and whether the courier itself rides on or presses
 * against a body whose motion stays remote-driven. Decided on the newest predicted tick only.
 */
export function decidePlacement(world: WorldState, localId: string, input: InputState): Placement | null {
  const bodies = predictedBodies(world);
  const local = bodies.find(body => body.id === localId);
  if (!local || !isPlayer(local)) return null;
  const direction = Number(input.right) - Number(input.left);
  const group = new Set<string>([localId]);
  const pushed = new Set<string>();
  for (let changed = true; changed;) {
    changed = false;
    for (const body of bodies) {
      if (group.has(body.id)) continue;
      const support = supportOf(body, bodies);
      if (support && group.has(support.id)) {
        group.add(body.id);
        if (pushed.has(support.id)) pushed.add(body.id);
        changed = true;
        continue;
      }
      for (const member of bodies) {
        if (!group.has(member.id) || overlapY(member, body) <= EPS) continue;
        const onRight = Math.abs(member.x + member.w - body.x) <= CONTACT;
        const onLeft = Math.abs(body.x + body.w - member.x) <= CONTACT;
        if (!onRight && !onLeft) continue;
        const side = onRight ? 1 : -1;
        // The member's motion into the contact: the courier's held direction, a pushed member's displacement.
        const push = member.id === localId ? direction : Math.sign(member.vx);
        if (push !== side) continue;
        // A crate is carried along as soon as the courier leans on it. Another courier joins only
        // while the contact actually displaces it at less than its own walking speed: a stalemate,
        // a wall behind it, or a courier already walking away keep it on its remote-driven path.
        if (isPlayer(body) && !(Math.sign(body.vx) === side && Math.abs(body.vx) > EPS && Math.abs(body.vx) < PHYSICS.speed - 1)) continue;
        group.add(body.id);
        pushed.add(body.id);
        changed = true;
        break;
      }
    }
  }
  let anchorId: string | null = null;
  let pressing = false;
  const support = supportOf(local, bodies);
  if (support && !group.has(support.id)) anchorId = support.id;
  else {
    for (const body of bodies) {
      if (!isPlayer(body) || body.id === localId || group.has(body.id) || overlapY(local, body) <= EPS) continue;
      const onRight = Math.abs(local.x + local.w - body.x) <= CONTACT;
      const onLeft = Math.abs(body.x + body.w - local.x) <= CONTACT;
      if (!onRight && !onLeft) continue;
      const side = onRight ? 1 : -1;
      if (direction === side || (Math.abs(body.vx) > EPS && Math.sign(body.vx) === -side)) { anchorId = body.id; pressing = true; break; }
    }
  }
  return { anchorId, pressing, group, pushed };
}

interface TrajectoryPose extends Pose { vx: number; vy: number; grounded: boolean; supportId: string | null; /** Same place at both sampled ticks: the body rests in the prediction. */ resting?: boolean }

/** Sub-tick poses of every predicted body between the two sampled ticks. */
function trajectoryPoses(sample: TrajectorySample): { poses: Map<string, TrajectoryPose>; cornerTransitions: number; maxCorrection: number } {
  const before = predictedBodies(sample.lo);
  const after = predictedBodies(sample.hi);
  const sampled = sampleBodyInterval<Body>(before, after, sample.frac);
  const poses = new Map<string, TrajectoryPose>();
  for (const body of sampled.bodies) {
    const start = before.find(b => b.id === body.id) ?? body;
    const grounded = body.grounded && (start.grounded || sample.frac >= 0.9999);
    const supportId = isPlayer(body) ? grounded ? sample.frac < 1 && isPlayer(start) ? start.supportId ?? body.supportId : body.supportId : null : null;
    const resting = Math.abs(body.x - start.x) <= 1e-6 && Math.abs(body.y - start.y) <= 1e-6 && Math.abs(body.vx) <= 1e-6 && Math.abs(body.vy) <= 1e-6;
    poses.set(body.id, { x: body.x, y: body.y, vx: mix(start.vx, body.vx, sample.frac), vy: mix(start.vy, body.vy, sample.frac), grounded, supportId, resting });
  }
  return { poses, cornerTransitions: sampled.cornerTransitions, maxCorrection: sampled.maxCorrection };
}

interface Offset { x: number; y: number; rate: number }
interface Memory {
  bases: Map<string, Pose>;
  displayed: Map<string, Pose>;
  remoteBases: Map<string, Pose>;
}
/** A body the courier's group displaced: drawn from the prediction while pushed, then held where it was drawn until its delayed drawing arrives. */
/** `fold`: the hold sits on the resting prediction, so the frame it was taken on folds like a group rebase; a hold frozen at the last drawing does not. */
interface Hold extends Pose { fresh: boolean; fold: boolean }
interface Sticky { held: Hold | null; liveAt: number; gapX: number | null; distance: number | null; away: number; base: Pose | null; moved: boolean; rest: number }

/**
 * Places every drawn body. Remote-driven bodies sit on the fixed-delay interpolation;
 * the local courier and its group sit on the predicted trajectory (relative to a
 * remote anchor when riding or pushing one). Any change of target — a snapshot
 * rebase, a new input record rewriting a tick, an anchor or group change — is folded
 * into a per-body offset that decays over ~120 ms without ever moving a body against
 * its trajectory's own direction of travel.
 */
export class ScenePlacer {
  private offsets = new Map<string, Offset>();
  private lastMovingAt = new Map<string, number>();
  private memory: Memory | null = null;
  private corrections: { at: number; distance: number }[] = [];
  private correctionSum = 0;
  private correctionCount = 0;
  private correctionMax = 0;
  private guards = 0;
  private anchorChanges = 0;
  /** Local folds along the held direction: a catch-up means the replay applied the input late, a pull-back early. */
  private lateFolds = 0;
  private earlyFolds = 0;
  private lastPlacement: Placement | null = null;
  private sticky = new Map<string, Sticky>();
  /** Bodies handed back to their remote path this frame, with the pose they were held at. */
  private released = new Map<string, Pose>();
  private pushStarts = 0;
  private heldCount = 0;
  /** The local courier's horizontal residual is being kept this frame (resting body); frames so far. */
  private localHeldX = false;
  private restHoldFrames = 0;
  private holdReleases = { converged: 0, crossed: 0, away: 0, settled: 0, timeout: 0, gone: 0 };

  reset(): void { this.offsets.clear(); this.lastMovingAt.clear(); this.memory = null; this.lastPlacement = null; this.sticky.clear(); this.released.clear(); this.heldCount = 0; this.localHeldX = false; }

  place(args: PlaceArguments): PlacedScene {
    const { authority, localId, input, remote, trajectory, trajectoryPrevious, platforms, platformsRemote, gates, dtMs } = args;
    const memory = this.memory;
    const now = performance.now();

    // 1. Remote-driven bases: fixed-delay interpolation, riders of moving platforms shifted to the drawn platform.
    const remoteBases = new Map<string, TrajectoryPose>();
    for (const player of remote.players) if (activePlayer(player)) remoteBases.set(player.id, { x: player.x, y: player.y, vx: player.vx, vy: player.vy, grounded: player.grounded, supportId: player.supportId });
    for (const crate of remote.crates) remoteBases.set(crate.id, { x: crate.x, y: crate.y, vx: crate.vx, vy: crate.vy, grounded: crate.grounded, supportId: null });
    this.shiftPlatformRiders(remoteBases, remote, platforms, platformsRemote);

    // 2. Placement from the newest predicted tick; trajectory poses now and at the previous render tick.
    const placement = trajectory && localId ? decidePlacement(trajectory.hi, localId, input) : null;
    const group = placement?.group ?? new Set<string>();
    const current = trajectory ? trajectoryPoses(trajectory) : null;
    const previous = trajectoryPrevious ? trajectoryPoses(trajectoryPrevious) : null;
    if (placement && this.lastPlacement && placement.anchorId !== this.lastPlacement.anchorId) this.anchorChanges++;
    this.lastPlacement = placement;

    // 2b. Pushed bodies stay on the predicted path while the group displaces them. Afterwards their
    // delayed drawing is still catching up with where they were drawn, so they wait there instead of
    // sliding back onto it; the hand-over happens when it arrives (or crosses), and the residual fold
    // is then at most a pixel. A body whose delayed drawing moves away instead is released at once.
    const held = this.holdPushed(placement, group, remoteBases, current, memory, now);

    const bases = new Map<string, TrajectoryPose>();
    const displayed = new Map<string, PlacedBody>();
    const ids = [...remoteBases.keys()];
    for (const id of group) if (current?.poses.has(id) && !remoteBases.has(id)) ids.push(id);

    // 3. Remote-path bodies first (an anchor's drawn position is needed by the group).
    for (const id of ids) {
      if (group.has(id) && current?.poses.has(id)) continue;
      const base = remoteBases.get(id);
      if (!base) continue;
      const hold = held.get(id);
      const released = this.released.get(id);
      if (released) {
        // Hand-over frame: the residual is the gap between the holding place and the delayed drawing NOW
        // (≤ HOLD_TOLERANCE when it arrived, up to a step when it crossed), blended over ~120 ms.
        bases.set(id, base);
        const previousBase = memory?.remoteBases.get(id);
        const step = previousBase ? { x: base.x - previousBase.x, y: base.y - previousBase.y } : { x: 0, y: 0 };
        const offset = this.settle(id, { x: released.x - base.x, y: released.y - base.y }, step, dtMs, false, now);
        displayed.set(id, { id, x: base.x + offset.x, y: base.y + offset.y, vx: base.vx, vy: base.vy, grounded: base.grounded, supportId: base.supportId,
          path: 'remote', interpolated: { x: base.x, y: base.y }, offset });
        continue;
      }
      if (hold) {
        // Drawn at its holding place. The frame the push ends on folds exactly like a group member's
        // rebase (last base vs. the previous render tick on the current journal): a frame that had
        // overshot the stop eases back from the next frame on, together with the courier that pushed it.
        const wasDrawn = memory?.bases.get(id), previousPose = previous?.poses.get(id);
        const fold = hold.fresh && hold.fold && wasDrawn && previousPose ? { x: wasDrawn.x - previousPose.x, y: wasDrawn.y - previousPose.y } : null;
        hold.fresh = false;
        const offset = this.settle(id, fold, { x: 0, y: 0 }, dtMs, false, now);
        bases.set(id, { ...base, x: hold.x, y: hold.y });
        displayed.set(id, { id, x: hold.x + offset.x, y: hold.y + offset.y, vx: 0, vy: 0, grounded: base.grounded, supportId: base.supportId,
          path: 'held', interpolated: { x: base.x, y: base.y }, offset: { x: hold.x + offset.x - base.x, y: hold.y + offset.y - base.y } });
        continue;
      }
      bases.set(id, base);
      const previousBase = memory?.remoteBases.get(id);
      const fold = memory?.bases.get(id) && previousBase ? { x: memory.bases.get(id)!.x - previousBase.x, y: memory.bases.get(id)!.y - previousBase.y } : null;
      const step = previousBase ? { x: base.x - previousBase.x, y: base.y - previousBase.y } : { x: 0, y: 0 };
      const offset = this.settle(id, fold, step, dtMs, false, now);
      displayed.set(id, { id, x: base.x + offset.x, y: base.y + offset.y, vx: base.vx, vy: base.vy, grounded: base.grounded, supportId: base.supportId,
        path: 'remote', interpolated: { x: base.x, y: base.y }, offset });
    }

    // 4. The local group on the predicted trajectory, relative to a remote anchor when it has one.
    if (current && placement && localId) {
      const anchor = placement.anchorId;
      const anchorPose = anchor ? current.poses.get(anchor) : undefined;
      const anchorDrawn = anchor ? displayed.get(anchor) : undefined;
      const shift = anchorPose && anchorDrawn ? { x: anchorDrawn.x - anchorPose.x, y: anchorDrawn.y - anchorPose.y } : { x: 0, y: 0 };
      const anchorPrevious = anchor && previous ? previous.poses.get(anchor) : undefined;
      const anchorDrawnPrevious = anchor ? memory?.displayed.get(anchor) : undefined;
      const shiftPrevious = anchorPose && anchorDrawn
        ? anchorPrevious && anchorDrawnPrevious ? { x: anchorDrawnPrevious.x - anchorPrevious.x, y: anchorDrawnPrevious.y - anchorPrevious.y } : null
        : { x: 0, y: 0 };
      // Pressing against a remote body: the pair moves as the drawn remote moves. When the drawn
      // (delayed) body has not yet reached the contact its anchored position would pull the courier
      // TOWARD it, beyond the courier's own prediction; then the courier holds where it was drawn
      // until the drawn body arrives (one-way clamp). Pushing along with the drawn body is unaffected.
      const localPose = current.poses.get(localId);
      const localDrawnPrevious = memory?.displayed.get(localId);
      if (placement.pressing && anchorPose && anchorDrawn && localPose && localDrawnPrevious) {
        const towardAnchor = anchorPose.x > localPose.x ? 1 : -1;
        const hold = (anchored: number, own: number) => towardAnchor * (anchored - own) > EPS
          ? towardAnchor > 0 ? Math.min(anchored, localDrawnPrevious.x) : Math.max(anchored, localDrawnPrevious.x)
          : anchored;
        shift.x += hold(localPose.x + shift.x, localPose.x) - (localPose.x + shift.x);
        const localPrevious = previous?.poses.get(localId);
        if (shiftPrevious && localPrevious) shiftPrevious.x += hold(localPrevious.x + shiftPrevious.x, localPrevious.x) - (localPrevious.x + shiftPrevious.x);
      }
      for (const id of group) {
        const pose = current.poses.get(id);
        if (!pose) continue;
        const base: TrajectoryPose = { ...pose, x: pose.x + shift.x, y: pose.y + shift.y };
        bases.set(id, base);
        const previousPose = previous?.poses.get(id);
        const basePrevious = previousPose && shiftPrevious ? { x: previousPose.x + shiftPrevious.x, y: previousPose.y + shiftPrevious.y } : null;
        const wasDrawn = memory?.bases.get(id);
        const fold = wasDrawn && basePrevious ? { x: wasDrawn.x - basePrevious.x, y: wasDrawn.y - basePrevious.y } : null;
        const step = basePrevious ? { x: base.x - basePrevious.x, y: base.y - basePrevious.y } : { x: 0, y: 0 };
        if (id === localId && fold) {
          const direction = Number(input.right) - Number(input.left);
          const along = direction * fold.x;
          if (along < -1) this.lateFolds++; else if (along > 1) this.earlyFolds++;
        }
        // A resting local group keeps its horizontal residual (see settle): the stop is drawn where the
        // prediction stopped and never slides afterwards, in either direction. Not while ANCHORED to a
        // drawn remote body (riding it, pressing on it): there the drawn relation to that body — a rider
        // seated exactly on its base — outranks the stop, and the residual eases as before.
        const offset = this.settle(id, fold, step, dtMs, id === localId, now, !anchor);
        const remoteBase = remoteBases.get(id);
        displayed.set(id, { id, x: base.x + offset.x, y: base.y + offset.y, vx: pose.vx, vy: pose.vy, grounded: pose.grounded, supportId: pose.supportId,
          path: id === localId ? anchor ? 'anchored' : 'self' : 'group', interpolated: remoteBase ? { x: remoteBase.x, y: remoteBase.y } : { x: base.x, y: base.y }, offset });
      }
    }
    for (const id of [...this.offsets.keys()]) if (!displayed.has(id)) this.offsets.delete(id);
    for (const id of [...this.lastMovingAt.keys()]) if (!displayed.has(id)) this.lastMovingAt.delete(id);
    // Sub-1e-4 px float noise from mixing must not read as motion (an idle body's x is exactly constant).
    for (const body of displayed.values()) { body.x = Math.round(body.x * 1e4) / 1e4; body.y = Math.round(body.y * 1e4) / 1e4; }

    // 5. Collision-consistent drawing: the speculative group never overlaps a drawn body or solid by more than a pixel.
    const beforeGuard = new Map([...held.keys()].map(id => [id, displayed.get(id)]).filter((pair): pair is [string, PlacedBody] => !!pair[1]).map(([id, body]) => [id, { x: body.x, y: body.y }]));
    this.guard(displayed, group, authority, platforms, gates);
    // A guard shift of a waiting body moves its holding place too (its easing offset is untouched).
    for (const [id, hold] of held) {
      const body = displayed.get(id), before = beforeGuard.get(id);
      if (!body || !before) continue;
      const dx = body.x - before.x, dy = body.y - before.y;
      if (Math.abs(dx) < EPS && Math.abs(dy) < EPS) continue;
      hold.x += dx; hold.y += dy;
      const offset = this.offsets.get(id);
      if (offset) { offset.x -= dx; offset.y -= dy; }
      bases.set(id, { ...bases.get(id)!, x: hold.x, y: hold.y });
    }
    this.heldCount = held.size;

    this.memory = {
      bases: new Map([...bases].map(([id, pose]) => [id, { x: pose.x, y: pose.y }])),
      displayed: new Map([...displayed].map(([id, body]) => [id, { x: body.x, y: body.y }])),
      remoteBases: new Map([...remoteBases].map(([id, pose]) => [id, { x: pose.x, y: pose.y }])),
    };
    const players = new Map<string, PlacedBody>();
    const crates = new Map<string, PlacedBody>();
    for (const [id, body] of displayed) (authority.crates.some(crate => crate.id === id) ? crates : players).set(id, body);
    return { players, crates, placement, cornerTransitions: (current?.cornerTransitions ?? 0) + remote.cornerTransitions,
      cornerCorrection: Math.max(current?.maxCorrection ?? 0, remote.maxCorrection) };
  }

  /**
   * Sticky membership for displaced bodies. While the newest predicted tick still pushes a body it
   * is live (drawn by the group). Once it is not, it is held at the pose it was last drawn at until
   * its fixed-delay base arrives within HOLD_TOLERANCE or crosses it (residual ≤ 1 px), moves away
   * from it for HOLD_AWAY_FRAMES frames, or HOLD_TIMEOUT_MS pass since the last push — then it
   * returns to its remote-driven path and settle() blends the residual over ~120 ms.
   */
  private holdPushed(placement: Placement | null, group: Set<string>, remoteBases: Map<string, TrajectoryPose>, current: { poses: Map<string, TrajectoryPose> } | null, memory: Memory | null, now: number): Map<string, Hold> {
    const held = new Map<string, Hold>();
    this.released.clear();
    if (!placement) { if (this.sticky.size) { this.holdReleases.gone += this.sticky.size; this.sticky.clear(); } return held; }
    for (const id of placement.pushed) {
      const state = this.sticky.get(id);
      if (!state) { this.sticky.set(id, { held: null, liveAt: now, gapX: null, distance: null, away: 0, base: null, moved: false, rest: 0 }); this.pushStarts++; }
      else { state.held = null; state.liveAt = now; state.gapX = null; state.distance = null; state.away = 0; state.base = null; state.moved = false; state.rest = 0; }
    }
    for (const [id, state] of [...this.sticky]) {
      if (placement.pushed.has(id)) continue;
      const base = remoteBases.get(id);
      const lastDrawn = memory?.displayed.get(id);
      if (!base || !lastDrawn || group.has(id)) { this.sticky.delete(id); this.holdReleases.gone++; continue; }
      const rest = current?.poses.get(id);
      if (!state.held) {
        // The push ends at a whole tick, so the prediction already rests at the exact stop: hold there
        // (the transition frame folds any difference from the last drawing, exactly like the courier's
        // own rebase). A body that is NOT at rest in the prediction — moving on its own or pushed by
        // someone else — is frozen where it was last drawn instead of following a speculative path.
        state.held = rest?.resting ? { x: rest.x, y: rest.y, fresh: true, fold: true } : { x: lastDrawn.x, y: lastDrawn.y, fresh: true, fold: false };
      } else if (rest?.resting && (Math.abs(rest.x - state.held.x) > EPS || Math.abs(rest.y - state.held.y) > EPS)) {
        // A later authority may place the stop a tick further or nearer (the service applied the
        // release at a neighbouring tick, or a stalemate refuted the push): follow the RESTING
        // prediction so the body rebases together with the courier and the pair stays closed.
        state.held.x = rest.x; state.held.y = rest.y; state.held.fresh = true; state.held.fold = true;
      }
      const dx = state.held.x - base.x, dy = state.held.y - base.y;
      const distance = Math.hypot(dx, dy);
      const converged = Math.abs(dx) <= HOLD_TOLERANCE && Math.abs(dy) <= HOLD_TOLERANCE;
      const crossed = state.gapX !== null && Math.abs(state.gapX) > EPS && Math.abs(dx) > EPS && Math.sign(state.gapX) !== Math.sign(dx);
      state.away = state.distance !== null && distance > state.distance + 0.25 ? state.away + 1 : 0;
      // The delayed drawing normally travels up to the hold; once it has moved and then stops short
      // (the service stopped the body a tick earlier or later than replayed), wait no longer.
      const baseStep = state.base ? Math.hypot(base.x - state.base.x, base.y - state.base.y) : 0;
      if (baseStep > 0.05) { state.moved = true; state.rest = 0; } else if (state.moved) state.rest++;
      const release = converged ? 'converged' : crossed ? 'crossed' : state.away >= HOLD_AWAY_FRAMES ? 'away'
        : state.rest >= HOLD_SETTLED_FRAMES ? 'settled' : now - state.liveAt > HOLD_TIMEOUT_MS ? 'timeout' : null;
      if (release) { this.sticky.delete(id); this.holdReleases[release]++; this.released.set(id, { x: state.held.x, y: state.held.y }); continue; }
      state.gapX = dx; state.distance = distance; state.base = { x: base.x, y: base.y };
      held.set(id, state.held);
    }
    return held;
  }

  /** Remote bodies riding a moving platform follow the platform as drawn at the local time. */
  private shiftPlatformRiders(bases: Map<string, TrajectoryPose>, remote: RemoteSample, platforms: Platform[], platformsRemote: Platform[]): void {
    const deltas = new Map<string, Pose>();
    for (const platform of platforms) {
      if (!platform.motion) continue;
      const before = platformsRemote.find(p => p.id === platform.id);
      if (before && (Math.abs(platform.x - before.x) > EPS || Math.abs(platform.y - before.y) > EPS)) deltas.set(platform.id, { x: platform.x - before.x, y: platform.y - before.y });
    }
    if (!deltas.size) return;
    const players = new Map(remote.players.map(p => [p.id, p]));
    for (const [id, base] of bases) {
      let root: PlayerState | undefined = players.get(id);
      const seen = new Set<string>();
      while (root?.grounded && root.supportId && players.has(root.supportId) && !seen.has(root.id)) { seen.add(root.id); root = players.get(root.supportId); }
      let delta = root?.grounded && root.supportId ? deltas.get(root.supportId) : undefined;
      if (!delta && !players.has(id) && base.grounded) {
        const crate = remote.crates.find(c => c.id === id);
        const under = crate && platformsRemote.find(p => deltas.has(p.id) && Math.abs(crate.y + crate.h - p.y) < 0.75 && overlapX(crate, p) > EPS);
        if (under) delta = deltas.get(under.id);
      }
      if (delta) { base.x += delta.x; base.y += delta.y; }
    }
  }

  /** Fold a target discontinuity into the body's offset, then decay it for this frame.
   * `holdRest`: a body of the local group. While its trajectory does not move horizontally (the courier
   * stopped, or stands, or rides a still base) the horizontal residual is KEPT, not eased: a released key
   * reads as a dead stop, whatever the acknowledging authority later places the stop at (a tick further
   * or nearer). The residual is absorbed only once the body moves again, and then only toward the base —
   * decayAxis never crosses zero (never past the authority) and never steps against the travel. */
  private settle(id: string, fold: Pose | null, step: Pose, dtMs: number, local: boolean, now: number, holdRest = false): Pose {
    let offset = this.offsets.get(id);
    // The frame a body stops on shows exactly the stop; any horizontal residual eases from the
    // next frame on (a released key reads as an instant halt, never a same-frame step back).
    const wasMoving = (this.lastMovingAt.get(id) ?? -Infinity) >= now - 25;
    if (Math.abs(step.x) > 0.5) this.lastMovingAt.set(id, now);
    const justStopped = Math.abs(step.x) <= REST_STEP && wasMoving;
    if (fold && (Math.abs(fold.x) > 1e-4 || Math.abs(fold.y) > 1e-4)) {
      offset ??= { x: 0, y: 0, rate: 0 };
      offset.x += fold.x;
      offset.y += fold.y;
      offset.rate = Math.max(offset.rate, Math.hypot(offset.x, offset.y) / (DECAY_MS / 1000));
      this.offsets.set(id, offset);
      if (local) {
        const distance = Math.hypot(fold.x, fold.y);
        this.correctionSum += distance; this.correctionCount++; this.correctionMax = Math.max(this.correctionMax, distance);
        this.corrections.push({ at: now, distance });
        if (this.corrections.length > 256) this.corrections.shift();
      }
    }
    if (!offset) { if (local) this.localHeldX = false; return { x: 0, y: 0 }; }
    const magnitude = Math.hypot(offset.x, offset.y);
    if (magnitude < 1e-4) { this.offsets.delete(id); if (local) this.localHeldX = false; return { x: 0, y: 0 }; }
    const moving = Math.hypot(step.x, step.y) > 0.5;
    const scale = Math.min(1, Math.max(offset.rate, moving ? MIN_RATE_MOVING : MIN_RATE_STILL) * dtMs / 1000 / magnitude);
    const heldX = (holdRest && Math.abs(step.x) <= REST_STEP && Math.abs(offset.x) <= REST_HOLD_LIMIT) || justStopped;
    if (local) { this.localHeldX = heldX && Math.abs(offset.x) > 1e-4; if (this.localHeldX) this.restHoldFrames++; }
    offset.x = heldX ? offset.x : decayAxis(offset.x, -offset.x * scale, step.x);
    offset.y = decayAxis(offset.y, -offset.y * scale, step.y);
    if (Math.hypot(offset.x, offset.y) < 1e-4) { this.offsets.delete(id); return { x: 0, y: 0 }; }
    return { x: offset.x, y: offset.y };
  }

  /**
   * Rendered overlaps come only from the two time frames (the courier's lead vs remote delay)
   * meeting. Along the smaller penetration: a vertical one lifts the upper body (never pushing a
   * body down into a stack or a floor), a side one moves the
   * whole group back onto the drawn remote's face; two group members or two remote bodies are
   * separated by moving the upper/right one. No drawn body penetrates a platform or closed gate
   * by more than a pixel.
   * Every guard shift is folded into that body's offset, so the drawn body waits at the contact
   * while the delayed drawing catches up, instead of penetrating a little more each frame.
   */
  private guard(displayed: Map<string, PlacedBody>, group: Set<string>, authority: WorldState, platforms: Platform[], gates: GateDef[]): void {
    const rectOf = (body: PlacedBody): Rect => {
      const size = authority.players.find(p => p.id === body.id) ?? authority.crates.find(c => c.id === body.id);
      return { x: body.x, y: body.y, w: size?.w ?? 32, h: size?.h ?? 40 };
    };
    const solids: Rect[] = [...platforms, ...gates.filter(gate => !isGateOpen(gate, authority))];
    const move = (body: PlacedBody, dx: number, dy: number) => {
      const x = Math.min(Math.max(0, body.x + dx), WORLD_W - rectOf(body).w);
      dx = x - body.x;
      body.x = x; body.y += dy;
      body.offset = { x: body.offset.x + dx, y: body.offset.y + dy };
      const offset = this.offsets.get(body.id) ?? { x: 0, y: 0, rate: 0 };
      offset.x += dx; offset.y += dy;
      offset.rate = Math.max(offset.rate, Math.hypot(offset.x, offset.y) / (DECAY_MS / 1000));
      this.offsets.set(body.id, offset);
      this.guards++;
    };
    const members = [...group].map(id => displayed.get(id)).filter((body): body is PlacedBody => !!body);
    const moveGroup = (dx: number, dy: number) => { for (const each of members) move(each, dx, dy); };
    const others = [...displayed.values()].filter(body => !group.has(body.id));
    // A lift propagates one body per pass up a stack (a delayed eight-high stack descending onto the
    // predicted courier), so the pass budget follows the body count instead of a fixed three.
    for (let pass = 0; pass < Math.max(3, displayed.size + 2); pass++) {
      let shifted = false;
      for (const member of members) {
        const rect = rectOf(member);
        for (const other of others) {
          const otherRect = rectOf(other);
          const w = overlapX(rect, otherRect), h = overlapY(rect, otherRect);
          if (w <= OVERLAP_PX || h <= OVERLAP_PX) continue;
          if (h < w) {
            // Vertical: the upper body lifts, whichever it is; nothing is ever pushed down into a stack or floor.
            if (rect.y + rect.h / 2 < otherRect.y + otherRect.h / 2) moveGroup(0, -h); else move(other, 0, -h);
          } else moveGroup(rect.x + rect.w / 2 < otherRect.x + otherRect.w / 2 ? -w : w, 0);
          shifted = true;
        }
      }
      // Pairs within the group, and pairs of remote bodies whose two interpolations disagree by a
      // pixel or two while one of them switches path: the upper (or right-hand) body yields.
      for (const set of [members, others]) for (let i = 0; i < set.length; i++) for (let j = i + 1; j < set.length; j++) {
        const a = rectOf(set[i]), b = rectOf(set[j]);
        const w = overlapX(a, b), h = overlapY(a, b);
        if (w <= OVERLAP_PX || h <= OVERLAP_PX) continue;
        if (h < w) move(a.y < b.y ? set[i] : set[j], 0, -h);
        else move(a.x > b.x ? set[i] : set[j], w, 0);
        shifted = true;
      }
      for (const body of displayed.values()) {
        // No drawn body leaves the world: a residual kept on a resting body at a wall (or a delayed
        // drawing) is folded back inside the trail's edges.
        const edge = rectOf(body);
        const inside = Math.min(Math.max(0, edge.x), WORLD_W - edge.w);
        if (Math.abs(inside - edge.x) > EPS) { if (group.has(body.id)) moveGroup(inside - edge.x, 0); else move(body, inside - edge.x, 0); shifted = true; }
        const rect = rectOf(body);
        for (const solid of solids) {
          const w = overlapX(rect, solid), h = overlapY(rect, solid);
          if (w <= OVERLAP_PX || h <= OVERLAP_PX) continue;
          const dx = w < h ? (rect.x + rect.w / 2 < solid.x + solid.w / 2 ? -w : w) : 0;
          const dy = w < h ? 0 : rect.y + rect.h / 2 < solid.y + solid.h / 2 ? -h : h;
          if (group.has(body.id)) moveGroup(dx, dy); else move(body, dx, dy);
          shifted = true;
          break;
        }
      }
      if (!shifted) break;
    }
  }

  diagnostics() {
    const sorted = [...this.corrections].sort((a, b) => a.distance - b.distance);
    const localId = this.lastPlacement ? [...this.lastPlacement.group][0] : null;
    const local = localId ? this.offsets.get(localId) : undefined;
    return { correctionCount: this.correctionCount, meanCorrection: this.correctionCount ? this.correctionSum / this.correctionCount : 0,
      maximumCorrection: this.correctionMax, collisionSafeSnaps: this.guards, anchorChanges: this.anchorChanges, lateFolds: this.lateFolds, earlyFolds: this.earlyFolds,
      recentCorrectionP95: sorted.length ? sorted[Math.floor((sorted.length - 1) * 0.95)].distance : 0,
      lastCorrection: this.corrections.at(-1) ? { ...this.corrections.at(-1)! } : null,
      activeOffsets: this.offsets.size, decayMs: DECAY_MS, stepFloor: STEP_FLOOR,
      offset: local ? { x: local.x, y: local.y } : { x: 0, y: 0 },
      anchorId: this.lastPlacement?.anchorId ?? null, pressing: this.lastPlacement?.pressing ?? false, groupSize: this.lastPlacement?.group.size ?? 0,
      pushedIds: [...(this.lastPlacement?.pushed ?? [])], heldCount: this.heldCount, pushStarts: this.pushStarts, holdReleases: { ...this.holdReleases },
      holdTolerancePx: HOLD_TOLERANCE, holdTimeoutMs: HOLD_TIMEOUT_MS,
      restHeldX: this.localHeldX, restHoldFrames: this.restHoldFrames, restHoldLimitPx: REST_HOLD_LIMIT };
  }
}

/** Decay one axis of an offset without letting the drawn step oppose (or lose most of) the trajectory's step. */
function decayAxis(offset: number, change: number, step: number): number {
  if (Math.abs(offset) < 1e-4) return 0;
  if (Math.abs(step) > 0.005) {
    const sign = Math.sign(step);
    const limit = (1 - STEP_FLOOR) * Math.abs(step);
    if (sign * change < -limit) change = -sign * limit;
  }
  const next = offset + change;
  // Never overshoot through zero.
  return Math.sign(next) !== Math.sign(offset) ? 0 : next;
}
